import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconClipboard } from "@tabler/icons-react";
import { ConfirmDialog } from "components";
import { LangConstant, SystemConstant } from "const";
import { useCreateDepartmentChannel, useCreateUnitChannel } from "hooks";
import { CommonUtils } from "utils";

const CreateChannelDialog = ({ open, selectedUnit, selectedDepartment, departmentUserList, unitUserList, onClose }) => {
  const isUnit = Boolean(selectedUnit) && selectedDepartment === null;

  const { t: getLabel } = useTranslation();
  const channelDialog = getLabel(LangConstant.OBJ_CHANNEL_DIALOG, { returnObjects: true });

  const { createUnitChannel } = useCreateUnitChannel();
  const { createDepartmentChannel } = useCreateDepartmentChannel();

  const [channelForm, setChannelForm] = useState({
    name: selectedUnit?.unitName || selectedDepartment?.departmentName || "",
    mode: SystemConstant.CHANNEL_MODE.private.code,
    user: isUnit ? unitUserList[0] : departmentUserList[0],
    userList: isUnit ? unitUserList : departmentUserList,
  });

  const [confirmDialogState, setConfirmDialogState] = useState({ open: false, title: "" });

  const handleChangeManager = event => {
    const selectedUser = channelForm.userList.find(u => u.id === event.target.value || u.value === event.target.value);
    setChannelForm(currentState => ({ ...currentState, user: selectedUser || null }));
  };

  const handleChangeMode = event => {
    setChannelForm(currentState => ({ ...currentState, mode: event.target.value }));
  };

  const handleOpenCofirmDialog = () => {
    setConfirmDialogState(currentState => ({
      ...currentState,
      open: true,
      title: getLabel(isUnit ? channelDialog.unitConfirm : channelDialog.departmentConfirm),
    }));
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogState(currentState => ({ ...currentState, open: false }));
  };

  const handleCreateChannel = () => {
    const payload = {
      privateF: channelForm.mode,
      adminId: channelForm.user.id,
      groupType: SystemConstant.GROUP_TYPE.CHANNEL,
      groupId: CommonUtils.uuid(),
      onClose: onClose,
    };
    if (isUnit) {
      createUnitChannel({ ...payload, unitId: selectedUnit.unitId });
    } else {
      createDepartmentChannel({ ...payload, departmentId: selectedDepartment.departmentId });
    }
    handleCloseConfirmDialog();
  };

  useEffect(() => {
    setChannelForm(currentState => ({
      ...currentState,
      name: selectedUnit?.unitName || selectedDepartment?.departmentName || "",
      mode: SystemConstant.CHANNEL_MODE.private.code,
      user: isUnit ? unitUserList[0] : departmentUserList[0],
      userList: isUnit ? unitUserList : departmentUserList,
    }));
  }, [selectedUnit, selectedDepartment]);

  // ESC key handler
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = e => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Fragment>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
      <div className="modal show d-block" tabIndex="-1" style={{ zIndex: 1050 }}>
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" style={{ fontWeight: 600 }}>
                {getLabel(isUnit ? channelDialog.unitTitle : channelDialog.departmentTitle)}
              </h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 600 }}>
                  {getLabel(channelDialog.channelName)}
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <IconClipboard size={18} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    disabled
                    value={channelForm.name}
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 600 }}>
                  {getLabel(channelDialog.channelMode)}
                </label>
                <select
                  className="form-select"
                  value={channelForm.mode}
                  onChange={handleChangeMode}
                >
                  {Object.values(SystemConstant.CHANNEL_MODE).map((item, index) => (
                    <option key={index} value={item.code}>
                      {getLabel(item.label)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 600 }}>
                  {getLabel(channelDialog.channelManager)}
                </label>
                <select
                  className="form-select"
                  value={channelForm.user?.id || channelForm.user?.value || ""}
                  onChange={handleChangeManager}
                >
                  <option value="" disabled>
                    {getLabel(channelDialog.channelManager)}
                  </option>
                  {channelForm.userList &&
                    channelForm.userList.map((user, index) => (
                      <option key={index} value={user.id || user.value}>
                        {user.label || user.name || user.id}
                      </option>
                    ))}
                </select>
                {channelForm.userList && channelForm.userList.length === 0 && (
                  <small className="text-muted">{channelDialog.noUser}</small>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline-danger"
                style={{ flex: 1 }}
                onClick={onClose}
              >
                {getLabel(LangConstant.L_CANCEL)}
              </button>
              <button
                disabled={!channelForm.name || !channelForm.user}
                className="btn btn-primary"
                style={{ flex: 1, marginLeft: 12 }}
                onClick={handleOpenCofirmDialog}
              >
                {getLabel(LangConstant.L_CONFIRM_BUTTON)}
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirmDialogState.open}
        title={confirmDialogState.title}
        onDeny={handleCloseConfirmDialog}
        onConfirm={handleCreateChannel}
      />
    </Fragment>
  );
};

export default CreateChannelDialog;
